/**
 * WP-V2-WC — hook dev: ?world=1 uruchamia WorldDemoScene.
 *
 * Wywołanie: installWorldDevHook(game) z main (integration) albo
 * autoAttachWorldDev() po pojawieniu się window.__pieskowo.
 *
 * Nie modyfikuje registry/main — auto-attach czeka na globalną grę.
 */
import type Phaser from 'phaser';
import { WorldDemoScene } from './WorldDemoScene.js';

export function isWorldDevQuery(search = typeof window !== 'undefined' ? window.location.search : ''): boolean {
  try {
    return new URLSearchParams(search).get('world') === '1';
  } catch {
    return false;
  }
}

/**
 * Rejestruje i startuje WorldDemo (zatrzymuje inne aktywne sceny).
 * @returns true gdy hook zadziałał.
 */
export function installWorldDevHook(game: Phaser.Game): boolean {
  if (!isWorldDevQuery()) return false;

  const key = WorldDemoScene.KEY;
  if (!game.scene.getScene(key)) {
    game.scene.add(key, WorldDemoScene, false);
  }

  for (const s of game.scene.getScenes(true)) {
    const sk = s.scene.key;
    if (sk !== key) {
      try {
        game.scene.stop(sk);
      } catch {
        /* ignore */
      }
    }
  }

  game.scene.start(key);
  return true;
}

/**
 * Podpina się pod window.__pieskowo (ustawiane w main.ts) gdy ?world=1.
 * Bezpieczne do importu side-effect — no-op bez query i bez okna.
 */
export function autoAttachWorldDev(timeoutMs = 12_000): void {
  if (typeof window === 'undefined') return;
  if (!isWorldDevQuery()) return;

  const w = window as unknown as {
    __pieskowo?: Phaser.Game;
    __worldDevInstalled?: boolean;
  };

  if (w.__worldDevInstalled) return;

  const started = Date.now();
  const tick = (): void => {
    if (w.__worldDevInstalled) return;
    const game = w.__pieskowo;
    if (game) {
      // Poczekaj 1 klatkę — Boot/Preload mogą wystartować równolegle
      window.setTimeout(() => {
        if (w.__worldDevInstalled) return;
        if (installWorldDevHook(game)) {
          w.__worldDevInstalled = true;
        }
      }, 80);
      return;
    }
    if (Date.now() - started < timeoutMs) {
      window.setTimeout(tick, 50);
    }
  };
  tick();
}

// Side-effect: w przeglądarce z ?world=1 sam się podłączy, o ile ten moduł jest załadowany.
// Import w index.ts + dynamic import z main (gdy integration doda 1 linię) albo
// poniższy self-load gdy bundler i tak ewaluuje graph...
// Aby demo działało BEZ zmiany main, wstrzykujemy dynamiczny import przez
// MutationObserver nie zadziała. Zamiast tego: eksportujemy i wołamy z index.
autoAttachWorldDev();
